<template>
  <section v-if="dashboardLoading || indicators.length" class="cso-panel section-card" :class="scope.clsPrefix">
    <div class="section-card-title">供应链核心经营指标</div>
    <div v-if="dashboardLoading" class="loading-state">加载中...</div>
    <div v-else class="indicators-container">
      <div
        v-for="(item, index) in indicators"
        :key="index"
        class="indicator-item"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = -1"
      >
        <div class="indicator-main">
          <div class="indicator-name">{{ item.name }}</div>
          <div class="indicator-trend" :class="item.trendClass">
            <span class="trend-value">{{ item.trendValue }}</span>
            <span v-if="item.trendIcon" class="trend-icon">{{ item.trendIcon }}</span>
          </div>
        </div>
        <div class="indicator-value">
          <span class="value-num">{{ item.value }}</span>
          <span v-if="item.unit" class="value-unit">{{ item.unit }}</span>
        </div>
        <div class="indicator-mask" v-show="hoverIndex === index">
          <button
            type="button"
            class="smart-analysis-btn"
            @click.stop="$emit('smart-analysis', item)"
          >智能分析</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { REGION, groupByRegion, parseIndicator, wanStr, moneyTrendText } from '../utils/externalMetrics'

export default {
  name: 'CsoCoreIndicators',
  props: {
    dashboardData: {
      type: Object,
      default: () => ({})
    },
    dashboardLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cso-core-indicators'
      },
      hoverIndex: -1
    }
  },
  computed: {
    indicators() {
      const data = this.dashboardData || {}
      const ext = data.externalMetrics
      if (!ext || !Array.isArray(ext.data)) return []
      const group = groupByRegion(ext.data)[REGION.CORE_INDICATORS]
      if (!group || !group.length) return []
      return group.slice(0, 5).map((item) => {
        const p = parseIndicator(item)
        // 「本月销售额/本月采购额」金额单位由 元 转 万（值同样除以 10000）
        if (/本月(销售|采购)/.test(p.name) && p.unit === '元') {
          p.value = wanStr(p.rawValue)
          p.unit = '万'
          // 金额类环比只保留相对百分比（去掉 ¥ 金额前缀），避免挤占指标名
          p.trendValue = moneyTrendText((p.meta && p.meta.mom && p.meta.mom.delta_txt))
        }
        return p
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cso-core-indicators {
  flex-shrink: 0;
  padding: 12px 14px;
  .section-card-title {
    margin: 0 0 10px;
    font-size: 12px;
    font-weight: 700;
    color: #1f2937;
  }

  .loading-state,
  .error-state {
    padding: 20px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }

  .error-state {
    color: #dc2626;
  }

  .indicators-container {
    display: flex;
    flex-wrap: nowrap;
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: thin;
    scrollbar-color: rgba(74, 95, 243, 0.28) transparent;
  }

  .indicators-container::-webkit-scrollbar {
    height: 4px;
  }

  .indicators-container::-webkit-scrollbar-thumb {
    background: rgba(74, 95, 243, 0.28);
    border-radius: 999px;
  }

  .indicator-item {
    flex: 1;
    flex-shrink: 0;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #eff2fc;
    padding: 12px 16px;
    position: relative;
  }

  // 指标名在上、环比在下，垂直堆叠。环比行固定预留一行高度，
  // 保证「有环比」与「无环比」卡片：指标名行、指标值行对齐，卡片高度一致。
  .indicator-main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 8px;
    min-width: 0;
  }

  .indicator-name {
    font-size: 12px;
    font-family: PingFangSC-Medium;
    font-weight: 600;
    color: #333333;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .indicator-trend {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-family: PingFangSC-Regular;
    font-weight: 400;
    flex-shrink: 0;
    white-space: nowrap;
    // 固定高度：无环比时同样占一行，保证指标值行对齐、卡片等高
    height: 16px;
    line-height: 16px;

    &.trend-up {
      color: #73b950;
    }

    &.trend-down {
      color: #f44f4f;
    }

    &.trend-flat {
      color: #f4b13d;
    }
  }

  .trend-icon {
    font-size: 12px;
    font-weight: 900;
  }

  .trend-value {
    font-weight: 500;
  }

  .indicator-value {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .value-num {
    font-size: 24px;
    font-weight: 700;
    font-family: PingFangSC-Semibold;
    color: #333;
    line-height: 32px;
  }

  .value-unit {
    font-size: 12px;
    color: #333;
    font-weight: 400;
  }

  .indicator-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(237, 241, 251, 0.23);
    backdrop-filter: blur(1px);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .smart-analysis-btn {
    width: 64px;
    height: 24px;
    border: none;
    background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-family: PingFangSC-Regular;
    font-weight: 400;
    font-size: 12px;
    color: #ffffff;
    line-height: 12px;
  }
}

@media screen and (max-width: 1440px) {
  .rvc-cosmoplat-workspace-cso-core-indicators {
    .value-num {
      font-size: 18px;
    }

    .indicator-name {
      font-size: 11px;
    }
  }
}
</style>
